// Inbox › Messages (gap #3): every text and email with the company's customers, newest first, by
// status (new replies and the ones that couldn't be sent first to find), channel and customer; one
// message's detail with what happened to it; Send again for a failed one (and its copy), a reply
// opened is read (and can be marked unread), a reply from a number on no customer is said to be an
// existing customer's (messages.assignCustomer) or becomes a new one, and a new message or a reply to
// a customer. The stream keeps the list and its counts current.

import { useCallback, useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { liveStream } from '../connection/live.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { Loading } from '../shared/Loading.tsx';
import { NewCustomerForm } from '../shared/NewCustomer.tsx';
import { formFromSearch } from '../shared/new-customer-model.ts';
import {
  MESSAGE_EVENTS,
  STATUS_FILTERS,
  visibleFilters,
  bodyText,
  channelLine,
  errorWords,
  firstLine,
  isFailed,
  isReply,
  isUnread,
  isUnsent,
  mergeFirstPage,
  messageTime,
  momentText,
  purposeWords,
  replyWords,
  statusChip,
  theirAddress,
  whenText,
  type Message,
  type StatusFilter,
} from './messages-model.ts';
import type { Customer } from '../types.ts';
import { SendMessage } from './SendMessage.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useDesign } from '../blueprint/design.ts';
import { TalkTabs } from '../inbox/TalkTabs.tsx';
import './messages.css';
import '../blueprint/inbox.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

interface Page {
  messages: Message[];
  next: string | null;
}

/** Opened from a job sheet's "Open in Messages": only that job's (QA-391). */
/** Where Messages opens from elsewhere: one job's texts and emails (a job sheet, QA-391), or a customer's (their history). */
export type MessagesStart =
  { job: { id: string; number: string } } | { customer: { id: string; name: string } };

export function MessagesScreen({
  nav,
  timeZone,
  canSend,
  start,
}: {
  nav: NavProps;
  timeZone: string;
  /** messages.send: write to a customer and send a failed message again. */
  canSend: boolean;
  start?: MessagesStart | null;
}) {
  // While the company mirrors ServiceTitan nothing goes to a customer from here (st-mirror §3: sends
  // are refused), and a new customer is ServiceTitan's; whose text it is stays ours to say (BL-088)
  const mirroring = useMirror();
  const bp = useDesign() === 'blueprint';
  const [status, setStatus] = useState<StatusFilter>('all');
  const [channel, setChannel] = useState<'' | 'sms' | 'email'>('');
  const [who, setWho] = useState<{ id: string; name: string } | null>(
    start && 'customer' in start ? start.customer : null,
  );
  const [job, setJob] = useState<{ id: string; number: string } | null>(
    start && 'job' in start ? start.job : null,
  );
  const [list, setList] = useState<Message[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [failedCount, setFailedCount] = useState<number | null>(null);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const [heldCount, setHeldCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [writing, setWriting] = useState<{ customerId: string; to?: string } | 'pick' | null>(null);
  // a reply from a number on no customer: "Say whose it is" (an existing customer) or "Add as a customer"
  const [assigning, setAssigning] = useState<Message | null>(null);
  const [adding, setAdding] = useState<Message | null>(null);
  // a message opened that isn't in the list shown (a failed one's copy, under another filter)
  const [extra, setExtra] = useState<Message | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [texting, setTexting] = useState<{ ok: boolean; notSetUp: string } | null>(null);
  // a job sheet or a customer's history opened it again while it's showing: theirs, from the top
  useEffect(() => {
    if (!start) return;
    setJob('job' in start ? start.job : null);
    setWho('customer' in start ? start.customer : null);
    setStatus('all');
    setOpenId(null);
  }, [start]);
  useEffect(() => {
    if (!canSend) return;
    client
      .call<{ sms: boolean; notSetUp: string }>('messages.channels', {})
      .then((r) => setTexting({ ok: r.sms, notSetUp: r.notSetUp }))
      .catch(() => setTexting(null));
  }, [canSend]);

  const query = useCallback(
    (before?: string) => {
      const f: { direction?: 'in' | 'out'; status?: readonly string[] } =
        STATUS_FILTERS.find((x) => x.key === status)?.query ?? {};
      return client.call<Page>('messages.list', {
        limit: 50,
        ...(f.status ? { status: [...f.status] } : {}),
        ...(f.direction ? { direction: f.direction } : {}),
        ...(channel ? { channel } : {}),
        ...(who ? { customerId: who.id } : {}),
        ...(job ? { jobId: job.id } : {}),
        ...(before ? { before } : {}),
      });
    },
    [status, channel, who, job],
  );
  // An answer to a filter no longer showing is dropped (QA-555): a refresh in flight when the filter
  // changed would land the old filter's rows in the new list, and they'd stay.
  const current = useRef(query);
  current.current = query;
  useEffect(() => {
    let stale = false;
    setList(null);
    setError(null);
    query()
      .then((r) => {
        if (stale) return;
        setList(r.messages);
        setNext(r.next);
      })
      .catch((e: unknown) => !stale && setError(message(e)));
    return () => {
      stale = true;
    };
  }, [query]);
  // After a send, and live: the first page again, in place, with older pages opened kept below it.
  const listRef = useRef(list);
  listRef.current = list;
  const lastTick = useRef(tick);
  useEffect(() => {
    if (tick === lastTick.current) return;
    lastTick.current = tick;
    let stale = false;
    query()
      .then((r) => {
        if (stale) return;
        const merged = mergeFirstPage(listRef.current, r.messages);
        setList(merged);
        if (merged.length === r.messages.length) setNext(r.next);
      })
      .catch(() => undefined); // what's shown stays; the next event tries again
    return () => {
      stale = true;
    };
  }, [query, tick]);
  // The stream: a message queued, sent, failed or received, or replies read (debounced: a batch is one read).
  useEffect(() => {
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (!MESSAGE_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => setTick((t) => t + 1), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, []);
  useEffect(() => {
    // the counts on "Replies" (new ones) and "Couldn't send", whatever is shown: the server's own
    // count of all of them, not of a page
    const count = (input: Record<string, unknown>, set: (n: number | null) => void) =>
      client
        .call<{ total: number }>('messages.list', {
          ...input,
          limit: 1,
          ...(who ? { customerId: who.id } : {}),
          ...(job ? { jobId: job.id } : {}),
        })
        .then((r) => set(r.total))
        .catch(() => set(null));
    // a failed one already sent again is dealt with (unsent leaves it out)
    void count({ unsent: true }, setFailedCount);
    void count({ direction: 'in', unread: true }, setUnreadCount);
    // held while the company mirrors ServiceTitan: its pill shows only when there are some
    void count({ status: ['held_mirror'] }, setHeldCount);
  }, [query, who, job, tick]);

  const more = () =>
    next &&
    query(next)
      .then((r) => {
        if (current.current !== query) return; // the filter changed while it loaded
        setList((l) => [...(l ?? []), ...r.messages]);
        setNext(r.next);
      })
      .catch((e: unknown) => setError(message(e)));

  const open = list?.find((m) => m.id === openId) ?? (extra?.id === openId ? extra : null);
  /** "See the copy": from the list when it's there, else messages.get. */
  const openMessage = (id: string) => {
    setWriting(null);
    setAdding(null);
    setAssigning(null);
    if (list?.some((m) => m.id === id)) return setOpenId(id);
    void client
      .call<Message>('messages.get', { id })
      .then((m) => {
        setExtra(m);
        setOpenId(m.id);
      })
      .catch((e: unknown) => setError(message(e)));
  };
  /** A reply opened is read; "Mark as unread" puts it back. */
  const markRead = (m: Message, read: boolean) =>
    client
      .call('messages.markRead', { messageIds: [m.id], read })
      .then(() => {
        const at = read ? new Date().toISOString() : null;
        setList((l) => l && l.map((x) => (x.id === m.id ? { ...x, readAt: at } : x)));
        setUnreadCount((n) => (n === null ? n : Math.max(0, n + (read ? -1 : 1))));
      })
      // it stays bold, and opening it again tries again
      .catch(() => undefined);
  const unsent = failedCount;
  const done = (what: string) => {
    setWriting(null);
    setAdding(null);
    setAssigning(null);
    setToast(what);
    setTick((t) => t + 1);
  };

  return (
    <div className="app">
      {bp && (open || writing || adding || assigning) ? (
        // Blueprint: what's open is its own page, titled with it; the crumb goes back to the messages
        <Header
          nav={nav}
          title={
            assigning
              ? 'Whose text is this?'
              : adding
                ? 'New customer'
                : writing
                  ? 'New message'
                  : open
                    ? (open.customerName ?? theirAddress(open))
                    : 'Texts and email'
          }
          crumb={{
            back: () => {
              setOpenId(null);
              setWriting(null);
              setAdding(null);
              setAssigning(null);
            },
            list: 'Texts and email',
          }}
        />
      ) : bp ? (
        // Blueprint (BL-132): Calls and Texts and email are the Inbox's two tabs, each at its own address
        <Header nav={nav} title="Texts and email" toolbar>
          <TalkTabs nav={nav} />
        </Header>
      ) : (
        <Header
          nav={nav}
          title="Messages"
          lede="Every text and email with your customers, newest first. New replies and the ones that couldn’t be sent are first to find."
        />
      )}
      <main
        className={`page messages${open || writing || adding || assigning ? ' has-detail' : ''}${bp ? ' bp-ib' : ''}`}
      >
        {toast && (
          <p className="notice ok" role="status">
            {toast}
          </p>
        )}
        <div className="msg-cols">
          <section className="msg-list" aria-label="Messages">
            {bp && (
              <h2 className="h4">
                Messages{' '}
                {list && (
                  <span className="count">
                    {list.length}
                    {next ? '+' : ''}
                  </span>
                )}
              </h2>
            )}
            <div className="msg-filters">
              <div className="seg" role="group" aria-label="Show">
                {visibleFilters(heldCount, status).map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`btn quiet${status === f.key ? ' on' : ''}`}
                    aria-pressed={status === f.key}
                    onClick={() => setStatus(f.key)}
                  >
                    {f.label}
                    {f.key === 'failed' && unsent ? (
                      <span className="count bad" aria-label={`${unsent} couldn’t be sent`}>
                        {unsent > 99 ? '99+' : unsent}
                      </span>
                    ) : null}
                    {f.key === 'replies' && unreadCount ? (
                      <span className="count brand" aria-label={`${unreadCount} new`}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
              <label className="f msg-channel">
                <span className="sr-only">Channel</span>
                <select
                  className="field"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as typeof channel)}
                >
                  <option value="">Texts and email</option>
                  <option value="sms">Texts</option>
                  <option value="email">Email</option>
                </select>
              </label>
              {who && (
                <button
                  type="button"
                  className="chip say brand msg-who"
                  // one name: a hidden span beside the label read with a space before its comma (QA-549)
                  aria-label={`Only ${who.name}, show everyone`}
                  onClick={() => setWho(null)}
                >
                  Only {who.name} <span aria-hidden="true">×</span>
                </button>
              )}
              {job && (
                <button
                  type="button"
                  className="chip say brand msg-who"
                  aria-label={`Only job #${job.number}, show every job`}
                  onClick={() => setJob(null)}
                >
                  Only job #{job.number} <span aria-hidden="true">×</span>
                </button>
              )}
              {canSend && !mirroring && (
                <button
                  type="button"
                  className={bp ? 'ib-link msg-new' : 'btn soft msg-new'}
                  onClick={() => setWriting('pick')}
                >
                  New message
                </button>
              )}
              {canSend && mirroring && <p className="muted small">{MIRROR_LINE}</p>}
            </div>
            {error && <p className="error">{error}</p>}
            {list === null ? (
              !error && <Loading error={null} what="the messages" kind="stones" />
            ) : list.length ? (
              <>
                <ul className="msg-rows">
                  {list.map((m) => {
                    const chip = statusChip(m);
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          className={`msg-row${m.id === openId ? ' on' : ''}${isUnsent(m) ? ' failed' : ''}${isUnread(m) ? ' unread' : ''}`}
                          aria-current={m.id === openId ? 'true' : undefined}
                          onClick={() => {
                            setOpenId(m.id);
                            setWriting(null);
                            setAdding(null);
                            setAssigning(null);
                            if (isUnread(m)) void markRead(m, true);
                          }}
                        >
                          <span className="grow">
                            <b>{m.customerName ?? theirAddress(m)}</b>
                            {bp ? (
                              // Blueprint's line: what it was, then what it said
                              <span className="small muted msg-line">
                                {m.channel === 'sms' ? 'Text' : 'Email'},{' '}
                                {(isReply(m) ? replyWords(m) : purposeWords(m.purpose)).toLowerCase()}: “
                                {firstLine(m.subject ?? bodyText(m))}”
                              </span>
                            ) : (
                              <span className="small muted msg-line">
                                {m.channel === 'sms' ? 'Text' : 'Email'} ·{' '}
                                {isReply(m) ? replyWords(m) : purposeWords(m.purpose)} ·{' '}
                                {firstLine(m.subject ?? bodyText(m))}
                              </span>
                            )}
                          </span>
                          <span className="msg-side">
                            <span className="small muted">{whenText(messageTime(m), timeZone)}</span>
                            <span className={`chip say ${chip.tone}`}>{chip.text}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {next && (
                  <button
                    type="button"
                    className={bp ? 'ib-link msg-more' : 'btn soft msg-more'}
                    onClick={() => void more()}
                  >
                    Show older messages
                  </button>
                )}
              </>
            ) : (
              <p className="muted empty">
                {status === 'failed'
                  ? 'Nothing failed: every message went out.'
                  : status === 'replies'
                    ? 'No replies yet. When a customer texts back, it shows here.'
                    : job
                      ? `No texts or emails on job #${job.number} yet.`
                      : who
                        ? `No messages to ${who.name} yet.`
                        : 'No messages yet. Texts and emails to customers show here as they go.'}
              </p>
            )}
          </section>

          {writing === 'pick' ? (
            <PickCustomer
              onPick={(c) => setWriting({ customerId: c.id })}
              onCancel={() => setWriting(null)}
            />
          ) : writing ? (
            <div className="msg-detail">
              <button
                type="button"
                className={bp ? 'ib-link msg-back' : 'btn quiet msg-back'}
                onClick={() => setWriting(null)}
              >
                ‹ Messages
              </button>
              <SendMessage
                customerId={writing.customerId}
                {...(writing.to ? { to: writing.to } : {})}
                onSent={done}
                onCancel={() => setWriting(null)}
              />
            </div>
          ) : assigning ? (
            <AssignReply
              m={assigning}
              mirroring={mirroring}
              onDone={done}
              onCancel={() => setAssigning(null)}
            />
          ) : adding ? (
            <section className="msg-detail stone" aria-label="New customer">
              <button
                type="button"
                className={bp ? 'ib-link msg-back' : 'btn quiet msg-back'}
                onClick={() => setAdding(null)}
              >
                ‹ Messages
              </button>
              <h2 className={bp ? 'ib-vh' : undefined}>New customer</h2>
              <p className="small muted">From the number that texted: {theirAddress(adding)}.</p>
              {/* a new customer is ServiceTitan's while mirroring, as from Book and Calls (BL-088) */}
              {mirroring ? (
                <p className="muted small">{MIRROR_LINE}</p>
              ) : (
                <NewCustomerForm
                  start={formFromSearch(theirAddress(adding))}
                  who="this number"
                  consentNote="Consent (saved with who and when)"
                  submitLabel="Add customer"
                  // the new customer has the number, so this text goes under them too
                  link={(customerId) =>
                    client.call('messages.assignCustomer', { messageId: adding.id, customerId })
                  }
                  onDone={() => done('Added as a customer. This text and their next ones show under them.')}
                  onCancel={() => setAdding(null)}
                />
              )}
            </section>
          ) : open ? (
            <Detail
              key={open.id}
              m={open}
              timeZone={timeZone}
              canSend={canSend}
              mirroring={mirroring}
              onBack={() => setOpenId(null)}
              onOnly={(c) => {
                setWho(c);
                setOpenId(null);
              }}
              onReply={() =>
                open.customerId &&
                setWriting({ customerId: open.customerId, to: (isReply(open) && open.from) || open.to })
              }
              onMarkUnread={() => void markRead(open, false)}
              onSeeCopy={() => open.retriedBy && openMessage(open.retriedBy)}
              onAssign={() => setAssigning(open)}
              onAddCustomer={() => setAdding(open)}
              texting={texting}
              onRetried={done}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}

/** One message: where it went, what it said, what happened to it, and what can be done about it. */
function Detail({
  m,
  timeZone,
  canSend,
  mirroring,
  texting,
  onBack,
  onOnly,
  onReply,
  onMarkUnread,
  onSeeCopy,
  onAssign,
  onAddCustomer,
  onRetried,
}: {
  m: Message;
  timeZone: string;
  canSend: boolean;
  /** While mirroring: no sending again, writing back or adding a customer; saying whose it is stays. */
  mirroring: boolean;
  /** messages.channels; null when not known. */
  texting: { ok: boolean; notSetUp: string } | null;
  onBack: () => void;
  onOnly: (c: { id: string; name: string }) => void;
  onReply: () => void;
  onMarkUnread: () => void;
  /** A failed one sent again: open the copy that went in its place. */
  onSeeCopy: () => void;
  /** A reply from a number on no customer: say which customer it is from. */
  onAssign: () => void;
  /** Or: the new-customer form with that number. */
  onAddCustomer: () => void;
  onRetried: (what: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bp = useDesign() === 'blueprint';
  const chip = statusChip(m);
  const retried = !!m.retriedBy;
  const refused = m.errorCode === 'do_not_contact' || m.errorCode === 'consent_missing';
  const cantText = m.channel === 'sms' && texting !== null && !texting.ok;
  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('messages.retry', { id: m.id });
      onRetried(
        `Sent again: the ${m.channel === 'sms' ? 'text' : 'email'} to ${m.customerName ?? theirAddress(m)} is on its way.`,
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  // held while the company mirrored ServiceTitan: a person sends it once the company is live (§3.5)
  const release = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('messages.release', { ids: [m.id] });
      onRetried(
        `Released: the ${m.channel === 'sms' ? 'text' : 'email'} to ${m.customerName ?? theirAddress(m)} is on its way.`,
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  const reply = isReply(m);
  const steps: { at: string; text: string; tone?: 'bad' | 'ok' }[] = reply
    ? [
        { at: messageTime(m), text: 'Received' },
        ...(m.readAt ? [{ at: m.readAt, text: 'Opened in the office' }] : []),
      ]
    : [
        { at: m.queuedAt, text: 'Written' },
        ...(m.sentAt ? [{ at: m.sentAt, text: 'Handed to the provider' }] : []),
        ...(m.deliveredAt ? [{ at: m.deliveredAt, text: 'Delivered', tone: 'ok' as const }] : []),
      ];
  return (
    <section className="msg-detail stone" aria-label="The message">
      <button type="button" className={bp ? 'ib-link msg-back' : 'btn quiet msg-back'} onClick={onBack}>
        ‹ Messages
      </button>
      <div className="row between">
        {/* in Blueprint the Header's title says who; the status is the word under it */}
        <h2 className={bp ? 'ib-vh' : undefined}>{m.customerName ?? theirAddress(m)}</h2>
        <span className={`chip say ${chip.tone}`}>{chip.text}</span>
      </div>
      <p className="small muted">
        {channelLine(m)}
        {bp
          ? // Blueprint joins with commas, never a middle dot
            reply
            ? m.customerId
              ? ''
              : ', not on any customer yet'
            : `, ${purposeWords(m.purpose).toLowerCase()}`
          : reply
            ? m.customerId
              ? ''
              : ' · not on any customer yet'
            : ` · ${purposeWords(m.purpose)}`}
        {m.retryOf
          ? bp
            ? ', sent again in place of one that couldn’t be sent'
            : ' · sent again in place of one that couldn’t be sent'
          : ''}
      </p>
      {m.subject && (
        <p>
          <b>{m.subject}</b>
        </p>
      )}
      <p className={`msg-body${m.body.trim() ? '' : ' muted'}`}>{bodyText(m)}</p>
      {reply && m.purpose === 'stop' && (
        <p className="small muted">They texted STOP: no more texts to this number.</p>
      )}
      {reply && m.purpose === 'start' && (
        <p className="small muted">They texted START: texts to this number are back on.</p>
      )}
      <ol className="msg-steps" aria-label="What happened">
        {steps.map((s) => (
          <li key={s.text} className={s.tone ?? ''}>
            <span>{s.text}</span>
            <span className="small muted">{momentText(s.at, timeZone)}</span>
          </li>
        ))}
        {isFailed(m) && (
          <li className="bad">
            <span>{m.status === 'undelivered' ? 'Not delivered' : 'Couldn’t send'}</span>
            <span className="small">{errorWords(m)}</span>
          </li>
        )}
        {retried && (
          <li>
            <span>Sent again</span>
            <span className="small muted">A copy went in its place.</span>
          </li>
        )}
        {(m.status === 'queued' || m.status === 'sending') && (
          <li>
            <span>Waiting to go</span>
            <span className="small muted">It goes out in a moment.</span>
          </li>
        )}
      </ol>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {canSend && !mirroring && isFailed(m) && !retried && !refused && cantText && (
        <p className="small muted">{texting!.notSetUp}</p>
      )}
      <div className="row">
        {canSend && !mirroring && isFailed(m) && !retried && !refused && !cantText && (
          <button type="button" className="btn primary" disabled={busy} onClick={() => void retry()}>
            {busy ? 'Sending again…' : 'Send it again'}
          </button>
        )}
        {canSend && m.status === 'held_mirror' && (
          <button type="button" className="btn primary" disabled={busy} onClick={() => void release()}>
            {busy ? 'Sending…' : 'Send it now'}
          </button>
        )}
        {/* after STOP they can't be texted here, so no "Write back" */}
        {canSend && !mirroring && m.customerId && !(reply && m.purpose === 'stop') && (
          <button type="button" className={bp ? 'ib-link' : 'btn soft'} onClick={onReply}>
            {reply ? 'Write back' : `Write to ${m.customerName ?? 'them'}`}
          </button>
        )}
        {retried && (
          <button type="button" className={bp ? 'ib-link' : 'btn soft'} onClick={onSeeCopy}>
            See the copy
          </button>
        )}
        {canSend && reply && !m.customerId && m.from && (
          <>
            <button type="button" className={bp ? 'ib-link' : 'btn soft'} onClick={onAssign}>
              Say whose it is
            </button>
            {!mirroring && (
              <button type="button" className={bp ? 'ib-link' : 'btn soft'} onClick={onAddCustomer}>
                Add as a customer
              </button>
            )}
          </>
        )}
        {reply && m.readAt && (
          <button type="button" className={bp ? 'ib-link' : 'btn quiet'} onClick={onMarkUnread}>
            Mark as unread
          </button>
        )}
        {m.customerId && m.customerName && (
          <button
            type="button"
            className={bp ? 'ib-link' : 'btn quiet'}
            onClick={() => onOnly({ id: m.customerId!, name: m.customerName! })}
          >
            All messages with {m.customerName}
          </button>
        )}
      </div>
    </section>
  );
}

/** Find a customer (customers.list): for "New message", then write to them; or whose a reply is. */
function PickCustomer({
  onPick,
  onCancel,
  title = 'New message',
  label = 'Who to',
  lede,
  error,
}: {
  onPick: (c: { id: string; displayName: string }) => void;
  onCancel: () => void;
  title?: string;
  label?: string;
  lede?: string;
  error?: string | null;
}) {
  const [q, setQ] = useState('');
  const bp = useDesign() === 'blueprint';
  const [found, setFound] = useState<{ id: string; displayName: string; city: string | null }[] | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) return setFound(null);
    const t = setTimeout(() => {
      client
        .call<{ customers: { id: string; displayName: string; city: string | null }[] }>('customers.list', {
          query: q.trim(),
          limit: 8,
        })
        .then((r) => setFound(r.customers))
        .catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <section className="msg-detail stone" aria-label={title}>
      <button type="button" className={bp ? 'ib-link msg-back' : 'btn quiet msg-back'} onClick={onCancel}>
        ‹ Messages
      </button>
      <h2 className={bp ? 'ib-vh' : undefined}>{title}</h2>
      {lede && <p className="small muted">{lede}</p>}
      <label className="f">
        {label}
        <input
          className="field"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="A name, phone or address"
          autoFocus
        />
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {found && (
        <ul className="msg-rows" aria-label="Customers found">
          {found.length ? (
            found.map((c) => (
              <li key={c.id}>
                <button type="button" className="msg-row" onClick={() => onPick(c)}>
                  <span className="grow">
                    <b>{c.displayName}</b>
                    {c.city && <span className="small muted">{c.city}</span>}
                  </span>
                </button>
              </li>
            ))
          ) : (
            <li className="muted small">No customer matches “{q.trim()}”.</li>
          )}
        </ul>
      )}
      <div className="row">
        <button type="button" className={bp ? 'ib-link' : 'btn quiet'} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}

/**
 * "Say whose it is": a reply from a number on no customer goes under the customer it is from
 * (messages.assignCustomer). The number can be kept on one of their contacts, or a new one
 * (messages.assignNewContact), so their
 * next texts match; that gives no consent to text them. A number already another customer's is
 * refused in the api's words.
 */
function AssignReply({
  m,
  mirroring,
  onDone,
  onCancel,
}: {
  m: Message;
  /** While mirroring the text is attached, but its number isn't kept: contacts are ServiceTitan's. */
  mirroring: boolean;
  onDone: (what: string) => void;
  onCancel: () => void;
}) {
  const [picked, setPicked] = useState<Customer | null>(null);
  const bp = useDesign() === 'blueprint';
  const [keep, setKeep] = useState<string>('none');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const number = theirAddress(m);
  if (!picked)
    return (
      <PickCustomer
        title="Whose text is this?"
        label="Customer"
        lede={`From ${number}: “${firstLine(bodyText(m), 60)}”`}
        error={error}
        onPick={(c) =>
          void client
            .call<Customer>('customers.get', { id: c.id })
            .then((full) => {
              setPicked(full);
              // their one contact, when there is just one, else a choice
              setKeep(!mirroring && full.contacts.length === 1 ? full.contacts[0]!.id : 'none');
            })
            .catch((e: unknown) => setError(message(e)))
        }
        onCancel={onCancel}
      />
    );
  const missing = keep === 'new' && !newName.trim() ? 'Add the new contact’s name.' : null;
  const save = async () => {
    if (missing) return;
    setBusy(true);
    setError(null);
    try {
      // keeping the number writes the customer's contacts, refused while mirroring; attaching alone isn't
      if (keep === 'none')
        await client.call('messages.assignCustomer', { messageId: m.id, customerId: picked.id });
      else
        await client.call('messages.assignNewContact', {
          messageId: m.id,
          customerId: picked.id,
          addNumber: keep === 'new' ? { newContactName: newName.trim() } : { contactId: keep },
        });
      onDone(
        keep === 'none'
          ? `This text is under ${picked.displayName} now.`
          : `This text is under ${picked.displayName} now, and their next texts from ${number} will be too.`,
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="msg-detail stone" aria-label="Whose text is this?">
      <button type="button" className={bp ? 'ib-link msg-back' : 'btn quiet msg-back'} onClick={onCancel}>
        ‹ Messages
      </button>
      <h2>This is {picked.displayName}</h2>
      {mirroring && (
        <p className="small muted">
          Keeping {number} on their page: {MIRROR_LINE}
        </p>
      )}
      {!mirroring && (
        <fieldset className="msg-to">
          <legend>Keep {number} on their page?</legend>
          {picked.contacts.map((k) => (
            <label key={k.id} className="checkrow msg-way">
              <input type="radio" name="msg-keep" checked={keep === k.id} onChange={() => setKeep(k.id)} />
              <span className="grow">On {k.name}</span>
            </label>
          ))}
          <label className="checkrow msg-way">
            <input type="radio" name="msg-keep" checked={keep === 'new'} onChange={() => setKeep('new')} />
            <span className="grow">On a new contact</span>
          </label>
          {keep === 'new' && (
            <label className="f">
              Their name
              <input
                className="field"
                value={newName}
                maxLength={200}
                onChange={(e) => setNewName(e.target.value)}
              />
            </label>
          )}
          <label className="checkrow msg-way">
            <input type="radio" name="msg-keep" checked={keep === 'none'} onChange={() => setKeep('none')} />
            <span className="grow">No, just this text</span>
          </label>
        </fieldset>
      )}
      {keep !== 'none' && (
        <p className="small muted">Keeping the number doesn’t give consent: ask them before texting it.</p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button
          type="button"
          className="btn primary"
          disabled={busy || !!missing}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : `This is ${picked.displayName}`}
        </button>
        <button type="button" className={bp ? 'ib-link' : 'btn quiet'} onClick={() => setPicked(null)}>
          Someone else
        </button>
        <button type="button" className={bp ? 'ib-link' : 'btn quiet'} onClick={onCancel}>
          Cancel
        </button>
      </div>
      <p className="small muted" role="status" aria-live="polite">
        {missing && !busy ? missing : ''}
      </p>
    </section>
  );
}
